import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import Loader from "../../Loader";
import { useDispatch, useSelector } from "react-redux";
import { GetAllUsersName } from "../../Redux/Actions/userStatusService";
import moment from "moment";
import { Avatar, Box, Typography } from "@mui/material";

const StrategyPerformanceTable = ({ strategyObj }) => {
  
  const [strategy, setStrategy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    if (strategyObj) {
      const data = strategyObj.map((item, index) => {
        const { totalPortfolio, totalPnL, totalReturns, createdAt, totalCost } = item;
        const userInfo = item?.userRole?.user?.userInfo;
        const firstName = userInfo?.firstName || "";
        const lastName = userInfo?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim() || "Unknown User";
        const email = item?.userRole?.user?.email || "";
        const investedAmount = totalCost || 0;
        
        return {
          investorInfo: { name: fullName, email, initials: getInitials(fullName) },
          investedSince: investedAmount,
          portfolioValue: totalPortfolio || 0,
          gainLoss: totalPnL || 0,
          returnPercent: totalReturns || 0,
          createdAt: createdAt,
        };
      });

      setStrategy(data);
      setLoading(false);
    } else {
      setError("Error loading data. Please try again.");
      setLoading(false);
    }
  }, [strategyObj]);

  const columns = [
    {
      name: "investorInfo",
      label: "Investor Info",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: '#E0E0E0',
                  color: '#666666',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {value.initials}
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#18275A',
                    mb: 0.5,
                  }}
                >
                  {value.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#999999',
                  }}
                >
                  {value.email.length > 25 ? `${value.email.substring(0, 25)}...` : value.email}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
    },
    {
      name: "investedSince",
      label: "Invested in Fund Since",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                textAlign: 'center',
              }}
            >
              ${parseFloat(value || 0).toFixed(2)}
            </Typography>
          );
        },
      },
    },
    {
      name: "portfolioValue",
      label: "Portfolio Value in Fund",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                textAlign: 'center',
              }}
            >
              ${parseFloat(value || 0).toFixed(2)}
            </Typography>
          );
        },
      },
    },
    {
      name: "gainLoss",
      label: "Gain (Loss)",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const numValue =
            typeof value === "number" ? value : parseFloat(value || 0) || 0;
          const isNegative = numValue < 0;
          const absVal = Math.abs(numValue).toFixed(2);
          const display = `$${absVal}`;
          const text = isNegative ? `(${display})` : display;
          const color = isNegative ? "#D32F2F" : "#149E3B";
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color,
                textAlign: 'center',
              }}
            >
              {text}
            </Typography>
          );
        },
      },
    },
    {
      name: "returnPercent",
      label: "Return",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const num = typeof value === "number" ? value : parseFloat(value || 0) || 0;
          const isNegative = num < 0;
          const display = `${Math.abs(num).toFixed(2)}%`;
          const text = isNegative ? `(${display})` : display;
          const color = isNegative ? "#D32F2F" : "#149E3B";

          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color,
                textAlign: 'center',
              }}
            >
              {text}
            </Typography>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    filterType: "dropdown",
    responsive: "standard",
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: false,
    search: false,
    pagination: false,
    customTableBodyFooterRender: () => null,
    setTableProps: () => ({
      sx: {
        "& .MuiTableCell-head": {
          backgroundColor: "#FFFFFF",
          color: "#18275A",
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: "SF Pro Display",
          borderBottom: "1px solid #E0E0E0",
          padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
          borderBottom: "1px solid #E0E0E0",
          padding: "16px",
        },
        "& .MuiTableRow-root:hover": {
          backgroundColor: "#F8F9FA",
        },
      },
    }),
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : error ? (
        <p>{error}</p>
      ) : (
        <Box
          sx={{
            width: "100%",
            mt: 2,
            // remove default Paper/card background from MUIDataTable
            "& .MuiPaper-root": {
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
            },
          }}
        >
          <MUIDataTable
            data={strategy}
            options={options}
            columns={columns}
          />
        </Box>
      )}
    </>
  );
};

export default StrategyPerformanceTable;
